import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudTableViewConfig, CrudViewColumnType, ListGridOrderType } from '../../utils/list-grid';
import { CardDataMapConfig, ItemActions, ListMode } from '../crud-view';
import { HeadingComponentConfig, HeadingType } from '../../utils/heading';
import { Action, ActionBehavior, ActionResponse, RedirectUrlMode } from '../../utils/action-utils';
import { ButtonComponentConfig, ButtonType, } from '../../utils/button';
import { ActionButtonConfig } from '../../utils/title-toolbar/action-button-config.interface';
import { DropdownMenuComponentConfig } from '../../utils/dropdown-menu';
import { SwitchComponentConfig } from '../../utils/switch';
import { BreadcrumbOption } from 'ng-zorro-antd/breadcrumb';
import { InputGroupComponentConfig } from '../../utils/input-group';
import { IconType } from '../../utils/icon';
import { TemplatingService } from '../../services/templating/templating.service';
import { NzUploadFile } from 'ng-zorro-antd/upload';

import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import * as moment from 'moment-timezone';


export enum ComponentActions {
  ImportData = 'importData',
  ExportData = 'exportData'
}

export enum FileType {
  CSV = 'CSV',
  Excel = 'Excel',
  Unknown = 'Unknown'
}

@Component({
  selector: 'rappider-list-wrapper',
  templateUrl: './list-wrapper.component.html',
  styleUrls: ['./list-wrapper.component.scss']
})
export class RappiderListWrapperComponent implements OnInit, OnChanges {
  @Input() listMode = ListMode.Card;

  /* Crud configs */
  @Input() listGridConfig: CrudTableViewConfig;
  @Input() cardListConfig: CardDataMapConfig;
  @Input() isDataLoading = false;
  @Input() isListGridBorderless = true;
  @Input() redirectUrlData: Record<string, unknown>;
  /* data  */
  @Input() data: Record<string, unknown | any>[];
  /* flag to display or hide the toolbar */
  @Input() displayToolbar = false;
  /* explicit option to pass to the toolbar in order to set the visibility of back button */
  @Input() displayToolbarBackButton = false;
  /** title bar */
  @Input() titleBarActionMenu?: DropdownMenuComponentConfig;
  @Input() titleBarSwitchSettings: SwitchComponentConfig;

  /* flag to display breadcrumb under title */
  @Input() displayBreadCrumb = true;
  @Input() breadCrumbOptions: BreadcrumbOption[] | string[] | string;
  @Input() titleBarActionButtons?: ButtonComponentConfig[] = [];

  /* config to set title according to crud pages */
  @Input() mainTitleConfig: HeadingComponentConfig;

  @Input() showCreateButton = true;
  @Input() createButtonTitle = 'Create';
  @Input() addListGridItemDefaultActions = true;
  @Input() addCardItemDefaultActions = true;

  @Input() allowDataImport? = false;

  @Input() importDataConfig?: any = {
    title: 'Import Data',
    helpTitle: 'How to Import Data',
    helpDescription: 'Import data from a file',
    downloadTemplateURL: '/assets/templates/sample_template.csv',
    downloadedTemplateFilename: 'template.csv',
    okText: 'Import',
    cancelText: 'Cancel',
    previewTableMaxHeight: '300px',
    excelImportConfig: {
      importSheetIndex: 0,
      sliceRowsFromSheet: 4, // Remove rows above row N (zero-indexed)
    },
    validationFunction: (item: any, data: any[]) => !!item, // Example validation function, validates each row
  };

  @Output() itemDeleted = new EventEmitter<any>();
  @Output() itemUpdated = new EventEmitter<any>();
  @Output() titleBarActionButtonClicked = new EventEmitter<ButtonComponentConfig | Action>();
  @Output() cardListItemActionClicked = new EventEmitter<any>();
  @Output() cardListItemSelected = new EventEmitter<any>();
  @Output() dataImported = new EventEmitter<any[]>();
  @Output() createValidEntity = new EventEmitter<any>();
  @Output() columnActionClick = new EventEmitter<ActionResponse>();
  @Output() listActionClick = new EventEmitter<ActionResponse>();


  /* Active item id in the path for editing or displaying the details of an item */
  activeItemId?: string;
  activeItem?: Record<string, unknown>;

  titleBarRadioButtonSettings = [
    {
      key: '<i class="fa-light fa-table-list"></i>',
      value: 'grid-view',
      tooltipText: 'Grid View'
    },
    {
      key: '<i class="fa-thin fa-grid-2"></i>',
      value: 'card-view',
      tooltipText: 'Card View'
    }
  ];

  cardListSearchText: string;
  filteredDataForCardMode: Object[];

  cardSearchInputConfig: InputGroupComponentConfig = {
    textbox: {
      placeholder: 'Search'
    },
    suffixIcon: {
      name: 'fas fa-search',
      type: IconType.FontAwesome
    }
  };

  ListMode = ListMode;

  /* Import Data modal visibility */
  isImportDataModalVisible = false;
  /* Files that are selected/dropped to import */
  importedFileList: NzUploadFile[] = [];
  importedEntities: any[] = [];
  listGridConfigForImportData: CrudTableViewConfig;
  defaultPreviewTableMaxHeight = '200px';
  importDataButton: ActionButtonConfig = {
    key: 'importData',
    text: this.importDataConfig?.importButtonText || 'Import Data',
    icon: {
      name: this.importDataConfig?.importButtonIcon || 'fa-sharp fa-regular fa-upload',
      type: IconType.FontAwesome,
    },
    type: this.importDataConfig?.importButtonType || ButtonType.Default
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private templatingService: TemplatingService
  ) { }

  /* get data and set view mode on init */
  ngOnInit(): void {
    this.getPathParameters();

    if (this.cardListConfig) {
      this.getCardDataForCardMode();
    } else {
      this.titleBarRadioButtonSettings = [];
    }

    if (this.allowDataImport) {
      // Add import data button to the title bar
      if (!this.titleBarActionButtons?.find(button => button.key === ComponentActions.ImportData)) {
        this.titleBarActionButtons.push(this.importDataButton);
      }

      const validationColumn = {
        title: 'Valid?',
        fieldName: '_isValid',
        type: CrudViewColumnType.Boolean,
        width: '50px'
      };

      const indexColumn = {
        title: '#',
        fieldName: '_index',
        type: CrudViewColumnType.Number,
        width: '50px'
      };

      // Set the preview table max height
      this.listGridConfigForImportData = {
        ...this.listGridConfig,
        order: {
          fieldName: '_index',
          type: ListGridOrderType.Ascending
        },
        columns: [indexColumn, validationColumn, ...(this.listGridConfig?.columns ?? [])],
        scroll: { yAxis: this.importDataConfig?.previewTableMaxHeight || this.defaultPreviewTableMaxHeight },
        itemActions: [],
        listActions: [],
      };
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getCardDataForCardMode();
  }

  /* get path parameters for view mode and editing item id */
  getPathParameters() {
    /* active item id is read from the path in order to show data to edit, e.g. /projects/edit/{id} */
    this.activeItemId = this.route.snapshot?.params?.itemId;
  }

  getCardDataForCardMode() {
    if (
      this.cardListSearchText && this.cardListSearchText.trim() &&
      this.data?.length &&
      this.listGridConfig
    ) {
      let filteredData;
      if (this.listGridConfig.multipleSearchFields && this.listGridConfig.multipleSearchFields.length > 0) {
        filteredData = this.data.filter(item =>
          this.listGridConfig.multipleSearchFields.some(field => item[field]?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase()))
        );
      } else if (this.listGridConfig.defaultSearchField) {
        filteredData = this.data.filter(item =>
          item[this.listGridConfig.defaultSearchField]?.toLowerCase().includes(this.cardListSearchText.trim().toLowerCase())
        );
      }
      this.filteredDataForCardMode = filteredData?.map(item => ({
        ...item,
        image: this.getCardImage(item).image,
        subtitles: this.getCardSubtitles(item)
      })) || [];
    }
    this.filteredDataForCardMode = this.data?.map(item => ({
      ...item,
      image: this.getCardImage(item).image,
      subtitles: this.getCardSubtitles(item)
    })) || [];
  }

  onSearchValueChange(searchText: string) {
    this.getCardDataForCardMode();
  }

  getCardImage(item: any) {
    const imageField = this.listGridConfig?.columns?.find(column => column.type === 'image');
    return {
      image: {
        source: item[imageField?.fieldName],
        customSizeSettings: {
          width: '100%',
          height: '8rem'
        },
      },
    };
  }

  /* on delete */
  onItemDeleted(deletedItemAction: { action: Action; data: Record<string, unknown> }) {
    if (deletedItemAction?.data?.id) {
      this.itemDeleted.emit(deletedItemAction?.data);
    }
  }

  onListGridListActionDropdownItemClick(item: any) {
    console.log(item);
  }

  onListGridListActionClick(action: Action, data: any) {
    this.listActionClick.emit({ action, data });
  }


  onListGridColumnActionClick(actionData: { action: Action; data: any }): void {
    if (actionData.action?.behavior === ActionBehavior.Route) {
      this.redirectToUrlByAction(actionData.action, actionData.data);
    } else if (actionData.action?.name === ItemActions.Delete) {
      this.itemDeleted.emit(actionData);
    } else if (actionData.action?.behavior === ActionBehavior.Emit) {
      this.columnActionClick.emit(actionData);
    }

  }

  onTitleBarRadioButtonClick(selectedValue: string) {
    if (selectedValue === 'card-view') {
      this.listMode = ListMode.Card;
    } else {
      this.listMode = ListMode.Grid;
    }
  }

  onTitleBarActionButtonClick(action: ButtonComponentConfig) {
    console.log(action);
    // Actions that are controlled by this component
    if (action?.key === ComponentActions.ImportData) {
      console.log('Import Data');
      this.isImportDataModalVisible = true;
    } else if (action?.behavior === ActionBehavior.Route) {
      this.router.navigateByUrl(action.redirectUrl);
    } else if (action?.behavior === ActionBehavior.Emit) {
      this.titleBarActionButtonClicked.emit(action);
    }
  }

  getCardSubtitles(cardData: any): HeadingComponentConfig[] {
    if (this.cardListConfig?.getSubTitlesFunction) {
      return this.cardListConfig?.getSubTitlesFunction(cardData);
    } else if (this.cardListConfig?.subtitleFieldNames?.length) {
      return this.cardListConfig?.subtitleFieldNames?.map(subtitleFieldName => <HeadingComponentConfig>{
        content: cardData[subtitleFieldName],
        type: HeadingType.H6
      }) || [];
    }
    return [];
  }

  onCardViewActionClick(action: any, cardData: any) {
    const buttonAction = action?.button || action;
    if (buttonAction?.behavior === ActionBehavior.Route) {
      this.redirectToUrlByAction(buttonAction, cardData);
    } else if (buttonAction?.behavior === ActionBehavior.Emit) {
      if (buttonAction?.name === ItemActions.Delete) {
        this.itemDeleted.emit({ action: buttonAction, data: cardData });
      } else {
        this.cardListItemActionClicked.emit({ action: buttonAction, data: cardData });
      }
    }
  }

  redirectToUrlByAction(action: Action, data?: any) {
    if (action?.redirectUrlMode === RedirectUrlMode.Navigate) {
      /* navigate */
      let url = action?.redirectUrl;
      if (url) {
        if (data) {
          url = this.templatingService.execTemplate(url, data);
        }
        this.router.navigateByUrl(url);
      }
    }
  }

  onCardViewItemSelected(item: any) {
    console.log(item);
    if (this.cardListConfig?.cardItemClickBehavior === ActionBehavior.Route) {
      this.redirectToUrlByAction(
        {
          behavior: ActionBehavior.Route, redirectUrlMode: RedirectUrlMode.Navigate,
          redirectUrl: this.cardListConfig?.cardItemRouterLink || ''
        },
        item
      );
    } else {
      this.cardListItemSelected.emit(item);
    }
  }


  // #region Import Data
  onImportDataCancelled() {
    this.isImportDataModalVisible = false;
    this.resetImportData();
  }

  onImportDataSubmitted() {
    this.isImportDataModalVisible = false;
    this.processImportedDataWithState();
    this.resetImportData();
  }

  resetImportData() {
    this.importedFileList = [];
    this.importedEntities = [];
  }

  /**
   * For each row with _isValid : true, trigger an NGRX action after transforming the data with provided JSONata
   *
   * @memberof RappiderListWrapperComponent
   */
  processImportedDataWithState() {

    const validEntities = this.importedEntities?.filter((entity) => !!entity._isValid) || [];

    //  Transform the data with JSONata if provided
    validEntities.forEach((entity: any) => {
      // blacklisted fields, e.g. _isValid field
      const blacklistedFields = ['_isValid', '_index'];
      blacklistedFields.forEach((field) => delete entity[field]);

      // remove additional fields that are not in the list grid config
      Object.keys(entity).forEach((key) => {
        if (!this.listGridConfigForImportData.columns.find((column) => column.fieldName === key)) {
          delete entity[key];
          console.log(`Removed field: ${key}`);
        }
      });

      // Emit each valid entity to the parent component
      this.createValidEntity.emit(entity);
    });

    // Emit the valid entities
    this.dataImported.emit(this.importedEntities);
  }

  downloadCSVTemplate(): void {
    if (this.listGridConfig?.columns?.length) {
      // Extract the column titles from the config
      const headers = this.listGridConfig?.columns?.map(column => column.title);

      // Convert the headers into a single CSV row
      const csvContent = [headers.join(',')].join('\n');

      // Create a Blob from the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      // Create a link element to download the Blob as a file
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'import-template.csv');

      // Append the link to the document, trigger a click, and remove the link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);
    } else {
      console.error('No columns found in the list grid config');
    }
  }

  /**
   * Process the uploaded file and import entities from it
   *
   * @param {NzUploadFile} file
   * @memberof RappiderListWrapperComponent
   */
  beforeUpload = (file: NzUploadFile): boolean => {
    this.importedFileList = [file];
    if (this.getFileType(file) === FileType.CSV) {
      this.replaceHeadersAndImportEntitiesFromCVS(file);
    } else if (this.getFileType(file) === FileType.Excel) {
      this.convertExcelToCsv(file)
        .then((csvData: string) => {
          // Convert the CSV data to NzUploadFile
          this.importEntitiesFromCsvContent(csvData);
        })
        .catch((error) => {
          console.error('Error converting Excel to CSV:', error);
        });

    }
    return false;
  };


  /**
   * Converts CSV data to an NzUploadFile object.
   * @param csvData - The CSV string to convert.
   * @param fileName - The name of the file to be used for the upload file.
   * @returns An NzUploadFile object.
   */
  convertCsvToNzUploadFile(csvData: string, fileName: string): NzUploadFile {
    // Step 1: Create a Blob from the CSV string
    const csvBlob = new Blob([csvData], { type: 'text/csv' });

    // Step 2: Create a File object from the Blob
    const csvFile = new File([csvBlob], fileName, { type: 'text/csv' });

    // Step 3: Create and return the NzUploadFile object
    const uploadFile: NzUploadFile = {
      uid: `${Date.now()}`,  // Unique ID for the file
      name: fileName,
      status: 'done',        // You can set status to 'done', 'uploading', or other values as needed
      originFileObj: csvFile // Attach the file object
    };

    return uploadFile;
  }


  getFileType(file: NzUploadFile): string {
    let fileType = '';
    switch (file.type) {
      case 'text/csv':
        fileType = FileType.CSV; // 'CSV';
        break;
      case 'application/vnd.ms-excel':
        fileType = FileType.Excel; // 'Excel';
        break;
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        fileType = FileType.Excel; // 'Excel';
        break;
      default:
        fileType = FileType.Unknown; // 'Excel'; 'Unknown';
        break;
    }
    return fileType;
  }

  /**
   * Replace headers with field names and import entities from the uploaded file
   *
   * @param {NzUploadFile} file
   * @memberof RappiderListWrapperComponent
   */
  replaceHeadersAndImportEntitiesFromCVS(file: NzUploadFile) {
    if (this.listGridConfig.columns.length) {
      const reader = new FileReader();

      reader.onload = (event: any) => {
        const fileContent = event.target.result;

        this.importEntitiesFromCsvContent(fileContent);
      };

      reader.onerror = (error: any) => {
        console.error('Error reading file:', error);
      };

      reader.readAsText(file as unknown as Blob);
    }
  }


  /**
   * Converts an Excel file to a CSV string, starting from row 5.
   * @param file - The Excel file to convert.
   * @returns A promise that resolves to the CSV string.
   */
  convertExcelToCsv(file: NzUploadFile): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event: any) => {
        try {
          const fileContent = event.target.result;

          const workbook = XLSX.read(fileContent, { type: 'binary' });

          // Get the first sheet
          const sheetName = workbook.SheetNames[this.importDataConfig?.importSheetIndex || 0];
          const worksheet = workbook.Sheets[sheetName];

          // // Convert sheet to JSON starting from row 5
          // const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          // const filteredData = data.slice(4);  // Remove rows above row 5 (zero-indexed)


          // Convert the data to CSV
          // const excelData = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 4 });
          const excelData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            range: 4,
            raw: false,
            // dateNF: 'yyyy-mm-dd',  // Define how Excel dates should be formatted
          });
          const csvData = XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet(excelData as any[][]));


          console.log(csvData);  // You can now save or download this CSV data

          resolve(csvData);
        } catch (error) {
          reject(error);
        };
      };

      reader.onerror = () => {
        reject(new Error('Failed to read the file.'));
      };

      reader.readAsBinaryString(file as unknown as Blob);
    });
  }

  convertExcelDate(serial: number): string {
    const epoch = new Date(1899, 11, 30);  // Excel epoch date (December 30, 1899)
    const date = new Date(epoch.getTime() + serial * 86400000);  // Add days in milliseconds
    return date.toISOString().split('T')[0];  // Return date in 'yyyy-mm-dd' format
  }

  importEntitiesFromCsvContent(fileContent: string) {
    let dateColumns = [];
    // Map titles to field names
    const headerMapping = this.listGridConfig.columns?.reduce((map, col, index) => {
      map[col.title] = col.fieldName;
      if (col?.importFileHeaderName) {
        map[col.importFileHeaderName.trim()] = col.fieldName;
      }
      if (col.type === 'date' || col.type === 'datetime') {
        // add the col index to the dateColumns array
        dateColumns.push(col.fieldName);
      }
      return map;
    }, {} as Record<string, string>);

    // Split file content into lines
    const rows = fileContent.split('\n');
    const originalHeaders = rows[0].split(',');

    // Replace titles with corresponding field names
    const updatedHeaders = originalHeaders.map((header) => headerMapping[header.trim()] || header);
    rows[0] = updatedHeaders.join(',');

    // Rejoin rows into a single CSV string
    const updatedCSV = rows.join('\n');

    // Parse the updated CSV
    Papa.parse(updatedCSV, {
      header: true,
      dynamicTyping: false,
      complete: (parsedCSV: any) => {
        const csvDataWithDateColumnsConverted = parsedCSV?.data?.map((row: any) => {
          // Convert date fields to ISO strings
          dateColumns.forEach((columnFieldKey) => {
            const excelDate = row[columnFieldKey];
            if (typeof excelDate === 'number') {
              // Convert the Excel date serial number to an actual date
              row[columnFieldKey] = this.convertExcelDate(excelDate);
            } else if (excelDate) {
              const convertedDate = moment(excelDate, 'DD/MM/YYYY').toISOString();
              row[columnFieldKey] = convertedDate; // new Date(excelDate).toISOString(); // local timezone
            }
          });
          return row;
        });

        const validatedEntities = csvDataWithDateColumnsConverted?.map((item: any, i) => ({
          ...item,
          _index: i + 1,
          _isValid: this.importDataConfig?.validationFunction ? this.importDataConfig?.validationFunction(item, this.data || []) : !!item,
        }));

        console.log(validatedEntities);
        this.importedEntities = validatedEntities || [];
      },
      error: (error: any, file: any) => {
        console.log(error, file);
      }
    });
  }

  // #endregion

}
